/**
 * Exit Intent Popup — Sitewide
 * ------------------------------
 * Renders on every page (via layout/theme.liquid). Fires when the visitor
 * shows intent to leave AND the cart currently has at least one item.
 *
 * Desktop: mouseleave at top of viewport
 * Mobile:  back button + app switch + scroll-up burst
 *
 * Lifecycle:
 *   1. Bail if this visitor has already submitted their email
 *   2. Wait ARM_DELAY_MS, then arm the exit triggers unconditionally
 *   3. On trigger → confirm the cart is non-empty → show modal
 *   4. On dismiss → stay armed, so the next exit attempt shows it again
 *   5. On submit → record the visitor as converted and never show it again
 *
 * SHOW-AGAIN POLICY
 * There is no once-per-day limit. A visitor who has not handed over their email
 * sees this popup on every exit attempt, for as long as they keep browsing. The
 * only way to switch it off is to submit the form — whether the discount code is
 * ever redeemed does not matter.
 *
 * Each dismissal opens a COOLDOWN_MS window in which every trigger is inert.
 * That window is what lets the visitor actually leave: on desktop, moving the
 * cursor up to the window controls is itself the mouseleave trigger, so without
 * a quiet period the popup would reappear on the way out every single time.
 *
 * Two triggers get longer windows of their own, for reasons in their sections
 * below: the scroll-up burst and the mobile app switch.
 *
 * The cart gate is evaluated at TRIGGER time, never at arm time. Arming used to
 * be gated on the cart count too, which meant a visitor who landed with an
 * empty cart and then added an item had no listeners attached and never saw
 * this popup at all — the add-to-cart happened after the arm decision and
 * nothing re-evaluated it.
 *
 * Only one popup may be on screen at a time, and the cart count is shared;
 * window.ThemePopups (assets/popup-manager.js) owns both, plus the scroll lock,
 * the background blur, the focus trap and the mobile back-button trap.
 */

(function () {
  'use strict';

  const POPUP_ID = 'exit-intent';

  const popup = document.getElementById('ExitIntentPopup');
  if (!popup) return;

  // The manager owns the mutex, the scroll lock and the cart count. Without it
  // there is no safe way to show this popup, so bail rather than half-work.
  const Popups = window.ThemePopups;
  if (!Popups) return;

  // =========================================
  // CONFIG
  // =========================================

  // How long after page load the exit triggers start listening. Hardcoded on
  // purpose: this used to be a theme setting, and a value saved in the theme
  // editor silently overrode the intended timing.
  const ARM_DELAY_MS = 2000;

  // Quiet window after each dismissal, during which no trigger fires.
  //
  // 3s rather than 2s for headroom. On desktop the cursor's trip up to the tab
  // bar or the window close button crosses clientY <= 0, which IS the trigger —
  // so the visitor's attempt to leave and the reason the popup reappears are the
  // same movement. That trip is normally well under a second; 3s leaves room for
  // a slower one instead of catching them on the way out.
  const COOLDOWN_MS = 1000000;

  const config = {
    discountCode: popup.dataset.discountCode || '',
    successMessage: popup.dataset.successMessage || '',
  };

  // =========================================
  // CONVERTED — ONCE THE EMAIL IS IN, NEVER AGAIN
  //
  // Written when the success view is shown, i.e. once the visitor has handed
  // over their email. There is no expiry: re-asking someone who already
  // subscribed reads as broken, and Shopify has their address either way.
  //
  // The flag lives in localStorage so it survives closing the tab, with
  // sessionStorage and then an in-memory value as fallbacks when storage is
  // blocked (private mode, strict cookie policies).
  //
  // This is per browser profile, and that is the ceiling for an anonymous
  // visitor — a different browser, a different device, or cleared site data all
  // start fresh. sections/exit-intent.liquid covers the cross-device case for
  // logged-in customers by checking the `exit-intent-cart` customer tag, which
  // the form itself applies. Neither layer helps an anonymous visitor in a new
  // browser, and nothing available to a theme can: identifying them would mean
  // fingerprinting or IP matching, and both are worse than the extra popup.
  //
  // Worst case in that gap is mild: they resubmit the same address, Shopify
  // treats it as a no-op rather than a duplicate, and the flag is then set in
  // that browser too.
  // =========================================
  const CONVERTED_KEY = 'exitIntentConverted';

  let memoryConverted = false;

  function hasConverted() {
    try {
      if (window.localStorage.getItem(CONVERTED_KEY)) return true;
    } catch (e) { /* localStorage blocked — try sessionStorage */ }

    try {
      if (window.sessionStorage.getItem(CONVERTED_KEY)) return true;
    } catch (e) { /* sessionStorage blocked too */ }

    return memoryConverted;
  }

  function markConverted() {
    memoryConverted = true;

    try {
      window.localStorage.setItem(CONVERTED_KEY, '1');
      return;
    } catch (e) { /* localStorage blocked — try sessionStorage */ }

    try {
      window.sessionStorage.setItem(CONVERTED_KEY, '1');
    } catch (e) { /* storage fully blocked — memory only */ }
  }

  if (hasConverted()) return;

  // =========================================
  // CART GATE — this popup applies only when the cart has items
  //
  // Server-rendered Liquid can be stale after an AJAX add/remove, so the count
  // always comes from the shared /cart.js cache. A null count means the check
  // failed, not that the cart is empty, so it must never pass the gate.
  // =========================================
  function gatePasses(count) {
    return count !== null && count >= 1;
  }

  // =========================================
  // COOLDOWN
  // Deliberately in memory only. A fresh page load is a fresh exit attempt, so
  // persisting this across loads would suppress the first trigger on every new
  // page for no reason.
  // =========================================
  // let lastClosedAt = 0;
      const LAST_CLOSED_KEY = 'exitIntentLastClosedAt';

    let memoryLastClosedAt = 0;

    function getLastClosedAt() {
      try {
        const v = window.localStorage.getItem(LAST_CLOSED_KEY);
        if (v) return parseInt(v, 10) || 0;
      } catch (e) { /* localStorage blocked — try sessionStorage */ }

      try {
        const v = window.sessionStorage.getItem(LAST_CLOSED_KEY);
        if (v) return parseInt(v, 10) || 0;
      } catch (e) { /* sessionStorage blocked too */ }

      return memoryLastClosedAt;
    }

    function setLastClosedAt(ts) {
      memoryLastClosedAt = ts;

      try {
        window.localStorage.setItem(LAST_CLOSED_KEY, String(ts));
        return;
      } catch (e) { /* localStorage blocked — try sessionStorage */ }

      try {
        window.sessionStorage.setItem(LAST_CLOSED_KEY, String(ts));
      } catch (e) { /* storage fully blocked — memory only */ }
    }

    let lastClosedAt = getLastClosedAt();

  function inCooldown() {
    return Date.now() - lastClosedAt < COOLDOWN_MS;
  }

  // =========================================
  // MODAL SHOW / HIDE
  // =========================================
  let lastFocusedElement = null;
  let showInProgress = false;

  // Reveal the modal. No cart gate here — callers own the check.
  // Returns false if another popup holds the screen, in which case nothing is
  // shown and the caller must not treat the popup as fired.
  function openModal() {
    if (!Popups.open(POPUP_ID)) return false;

    lastFocusedElement = document.activeElement;
    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');

    // Trap Tab inside the dialog. Without it this popup's aria-modal="true" was
    // a lie: keyboard users could Tab out to the page behind — including onto a
    // checkout button, which the blur's pointer-events:none does not protect.
    Popups.trapFocus(popup, popup.querySelector('input[type="email"]'));

    document.dispatchEvent(new CustomEvent('exit-intent:shown'));
    return true;
  }

  function showPopup() {
    if (hasConverted() || showInProgress || !popup.hidden || inCooldown()) return;

    // Another popup (or a theme drawer/modal) owns the screen → skip this
    // trigger entirely rather than stacking on top of it. Triggers stay armed
    // so the next exit attempt can still fire.
    if (!Popups.canOpen(POPUP_ID)) return;

    const fresh = Popups.isCartFresh();
    const cached = Popups.cartCount();

    // A fresh count that fails the gate means the sibling empty-cart popup owns
    // this trigger. Nothing to verify — don't spend a /cart.js round trip on it.
    if (fresh && !gatePasses(cached)) return;

    // Fast path: the count is FRESH and passes → open immediately, confirm in
    // the background. Freshness is what makes this safe. Acting on a stale
    // count is how the wrong popup used to appear right after an add-to-cart
    // and then retract itself a moment later.
    if (fresh) {
      if (!openModal()) return;

      showInProgress = true;
      Popups.refreshCart().then((count) => {
        showInProgress = false;

        // null = the re-check failed, not "cart is empty". The count that got
        // us here was seconds old, so trust it and keep the popup up rather
        // than yanking it off the screen over a network hiccup.
        if (count !== null && !gatePasses(count)) {
          hidePopup(); // cart emptied → retract, keep triggers armed
        }
      });
      return;
    }

    // Count unknown or stale → verify first so we never flash a popup that
    // doesn't apply to this cart.
    showInProgress = true;
    Popups.refreshCart().then((count) => {
      showInProgress = false;
      if (gatePasses(count)) openModal();
    });
  }

  function hidePopup() {
    popup.hidden = true;
    popup.setAttribute('aria-hidden', 'true');

    // Start the quiet window BEFORE releasing the manager, so a trigger that
    // fires in the same tick as the close cannot slip through.
    // lastClosedAt = Date.now();
     lastClosedAt = Date.now();
      setLastClosedAt(lastClosedAt);

    Popups.close(POPUP_ID);
    Popups.releaseFocus(lastFocusedElement);
  }

  function showSuccessView() {
    const captureView = popup.querySelector('[data-view="capture"]');
    const successView = popup.querySelector('[data-view="success"]');
    const successText = popup.querySelector('[data-success-text]');

    if (!captureView || !successView) return;

    // The email is in. Switch this visitor off permanently, and drop the
    // listeners so nothing can re-fire before they navigate away.
    markConverted();
    detachTriggers();

    captureView.hidden = true;
    successView.hidden = false;

    if (successText) {
      successText.textContent = config.successMessage.replace(/\[code\]/g, config.discountCode);
    }

    // No need to re-arm the trap — it recomputes the focusable set on each Tab,
    // so swapping which view is hidden is picked up on its own.
    const continueBtn = successView.querySelector('button');
    if (continueBtn) continueBtn.focus();

    document.dispatchEvent(new CustomEvent('exit-intent:converted'));
  }

  // =========================================
  // DEVICE DETECTION
  // =========================================
  const isTouchDevice = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

  // =========================================
  // DESKTOP TRIGGER: mouse exits top of viewport
  // The primary signal, and the one that repeats freely — it only fires on a
  // deliberate move out of the page, and the cooldown handles the rest.
  // =========================================
  function onMouseLeave(e) {
    if (e.clientY <= 0) showPopup();
  }

  // =========================================
  // MOBILE TRIGGER 1: Back button
  //
  // Deliberately does NOT re-arm the history trap. The manager schedules that
  // itself when the popup closes, and doing it from here raced the async open:
  // when the cart count needs verifying, showPopup() returns before the dialog
  // is on screen, so the "is a popup open?" guard saw nothing and pushed a
  // second entry. Two entries meant the visitor's first back press after
  // dismissing was silently eaten.
  // =========================================
  function onPopState() {
    showPopup();
  }

  // =========================================
  // TRIGGER: App switch / tab hidden
  //
  // Touch devices only. On desktop this fires on tab switches, minimising and
  // screen lock — none of which mean leaving — and the popup would open while
  // nobody is looking at it, so the visitor comes back to a dialog they never
  // triggered. Desktop exits are already covered by mouseleave.
  //
  // On mobile there is no mouseleave, so an app switch is a real exit signal.
  // But mid-session app switching is normal there (checking a message, comparing
  // prices), so this trigger gets a much longer window of its own than the
  // shared cooldown — otherwise ordinary multitasking would trip it repeatedly.
  //
  // The listener stays attached on desktop regardless: returning to the tab is
  // a good moment to refresh the cart count for the fast path.
  // =========================================
  const APP_SWITCH_COOLDOWN_MS = 30000;
  let lastAppSwitchAt = 0;

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      if (!isTouchDevice) return;
      if (Date.now() - lastAppSwitchAt < APP_SWITCH_COOLDOWN_MS) return;

      lastAppSwitchAt = Date.now();
      showPopup();
      return;
    }

    // Returned to the tab → refresh the shared count so the next exit attempt
    // can take the fast path. The cart may have changed in another tab, which
    // no cartUpdate event in this one would have reported.
    Popups.refreshCart();
  }

  // =========================================
  // MOBILE TRIGGER 3: Rapid scroll-up burst
  //
  // Noisier than the other triggers: a fast flick upwards is something visitors
  // do while shopping — to re-check a photo, to reach the menu — not only while
  // leaving. It used to fire at most once because the triggers detached after a
  // single showing; now that they persist, it needs a throttle of its own or an
  // ordinary browsing habit would keep summoning the popup.
  // =========================================
  const SCROLL_THROTTLE_MS = 15000;
  let lastScrollFireAt = 0;

  let lastScrollY = window.scrollY;
  let lastScrollTime = Date.now();

  function onScroll() {
    const now = Date.now();
    const scrollY = window.scrollY;
    const deltaY = lastScrollY - scrollY;
    const deltaTime = now - lastScrollTime;

    if (deltaY > 120 && deltaTime < 250 && scrollY < 300) {
      if (now - lastScrollFireAt >= SCROLL_THROTTLE_MS) {
        lastScrollFireAt = now;
        showPopup();
      }
    }

    lastScrollY = scrollY;
    lastScrollTime = now;
  }

  // =========================================
  // ATTACH / DETACH TRIGGERS (device-branched)
  //
  // There is no idle-timeout trigger. Sitting still is not an exit signal — a
  // visitor reading a blog post or a long description for a few minutes was
  // getting the popup for doing exactly what the page asks of them, and with
  // the once-a-day cap gone it would have repeated every timeout.
  //
  // Triggers are detached only once the visitor converts; dismissing the popup
  // leaves them armed on purpose.
  // =========================================
  function attachTriggers() {
    if (isTouchDevice) {
      Popups.armBackTrap();
      window.addEventListener('popstate', onPopState);
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('scroll', onScroll, { passive: true });
    } else {
      document.addEventListener('mouseleave', onMouseLeave);
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
  }

  function detachTriggers() {
    document.removeEventListener('mouseleave', onMouseLeave);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('popstate', onPopState);
  }

  // Arm unconditionally. The manager has already started warming the cart count
  // and showPopup() re-checks it on every trigger, so there is nothing to wait
  // for here — and nothing to gate on, since the cart can change at any point
  // after this timer runs.
  setTimeout(attachTriggers, ARM_DELAY_MS);

  // =========================================
  // CLOSE HANDLERS
  // =========================================
  popup.querySelectorAll('[data-exit-intent-close]').forEach((el) => {
    el.addEventListener('click', hidePopup);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !popup.hidden) hidePopup();
  });

  // The checkout gate (assets/shipping-confirmation-popup.js) outranks this
  // popup and evicts it rather than stacking on top. Step aside when named, so
  // the manager's bookkeeping and what's actually on screen stay in agreement.
  document.addEventListener('theme-popups:preempt', (e) => {
    if (e.detail && e.detail.id === POPUP_ID) hidePopup();
  });

  // =========================================
  // COPY CODE BUTTON
  // =========================================
  const copyBtn = popup.querySelector('[data-copy-code]');
  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(config.discountCode);
        const original = copyBtn.textContent;
        copyBtn.textContent = 'Copied!';
        setTimeout(() => { copyBtn.textContent = original; }, 2000);
      } catch (e) {
        const codeEl = popup.querySelector('[data-discount-display]');
        const range = document.createRange();
        range.selectNode(codeEl);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      }
    });
  }

  // =========================================
  // FORM SUBMIT
  // Form posts natively to hidden iframe (target="ExitIntentSink"
  // in snippet). Show success view after brief delay.
  // =========================================
  const form = popup.querySelector('#ExitIntentForm');
  if (form) {
    form.addEventListener('submit', function (e) {
      // DON'T preventDefault — let native submit happen into the iframe

      const emailInput = form.querySelector('input[type="email"]');
      if (!emailInput || !emailInput.value || !emailInput.checkValidity()) {
        return;
      }

      const submitBtn = form.querySelector('[type="submit"]');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Generating...';

      // Optimistic UI: show success after brief delay
      setTimeout(function () {
        showSuccessView();
      }, 600);
    });
  }
})();
